import { Link, useParams } from 'react-router-dom'
import { PipelineBadge } from '@agms/ui'
import type { PipelineStage } from '@agms/ui'
import { DocumentCenter } from '../components/lead/DocumentCenter'
import { PendingDocumentRequests } from '../components/lead/PendingDocumentRequests'
import { AccountActivityPanel } from '../components/account/AccountActivityPanel'
import { AppointmentsPanel } from '../components/account/AppointmentsPanel'
import { NotesPanel } from '../components/account/NotesPanel'
import { PageHeader, SurfaceCard } from '../components/ui'
import { usePermissions } from '../hooks/useAuth'
import { LeadAccountWorkspace } from '../components/account/lead/LeadAccountWorkspace'
import { MerchantAccountWorkspace } from '../components/account/merchant/MerchantAccountWorkspace'
import { RelatedAccountsPanel } from '../components/account/merchant/RelatedAccountsPanel'
import { useLeadContext, useMerchantContext } from './merchants/useMerchantData'
import type { Merchant } from './merchants/useMerchantData'

interface AccountPageProps {
  anchorKind: 'lead' | 'merchant'
}

export function AccountPage({ anchorKind }: AccountPageProps) {
  const { id } = useParams<{ id: string }>()
  const { hasRole } = usePermissions()
  const leadContextQuery = useLeadContext(anchorKind === 'lead' ? id ?? null : null)
  const merchantContextQuery = useMerchantContext(anchorKind === 'merchant' ? id ?? null : null)
  const contextQuery = anchorKind === 'lead' ? leadContextQuery : merchantContextQuery
  const accountContext = contextQuery.data
  const lead = accountContext?.lead ?? null
  const merchant = accountContext?.merchant ?? null
  const application = accountContext?.application ?? null

  if (contextQuery.isLoading) {
    return <p className="text-gray-500 py-8 text-center">Loading account...</p>
  }

  if (contextQuery.error || (anchorKind === 'lead' && !lead) || (anchorKind === 'merchant' && !merchant)) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Account not found"
          subtitle="The account record may have been removed or you may not have access."
          action={(
            <Link to={anchorKind === 'lead' ? '/leads' : '/merchants'} className="text-sm font-semibold text-blue-700 hover:text-blue-900">
              {anchorKind === 'lead' ? 'Back to Leads' : 'Back to Merchants'}
            </Link>
          )}
        />
      </div>
    )
  }

  if (lead && !merchant) {
    return <LeadAccountWorkspace leadId={lead.id} />
  }

  if (merchant && !lead) {
    return <MerchantAccountWorkspace merchantId={merchant.id} />
  }

  if (!lead || !merchant) {
    return null
  }

  const salesRep = hasRole('sales_rep')
  const sharedAnchor = !salesRep && anchorKind === 'merchant'
    ? { kind: 'merchant' as const, id: merchant.id }
    : { kind: 'lead' as const, id: lead.id }
  const notesAnchor = salesRep
    ? { kind: 'merchant' as const, id: merchant.id }
    : sharedAnchor
  const title = merchant.dba_name || lead.dba_name || lead.legal_name || 'Account'
  const subtitle = merchant.legal_name ?? lead.legal_name ?? undefined

  return (
    <div className="space-y-6">
      <PageHeader
        title={title}
        subtitle={subtitle}
        action={(
          <Link to={anchorKind === 'lead' ? '/leads' : '/merchants'} className="text-sm font-semibold text-blue-700 hover:text-blue-900">
            {anchorKind === 'lead' ? 'Back to Leads' : 'Back to Merchants'}
          </Link>
        )}
      />

      <SurfaceCard padding="p-5">
        <div className="flex flex-wrap items-start justify-between gap-5">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <PipelineBadge stage={lead.stage as PipelineStage} />
              <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold capitalize text-slate-700">
                {merchant.status}
              </span>
              <span className="text-sm text-slate-500">MID: {merchant.fiserv_mid ?? '—'}</span>
            </div>
            {application && (
              <p className="text-sm text-slate-500">
                Application {application.fiserv_as_ref_id ? `AS Ref ${application.fiserv_as_ref_id}` : application.id}
                {application.fiserv_status ? ` · ${application.fiserv_status}` : ''}
              </p>
            )}
          </div>
          <div className="grid min-w-64 gap-2 text-sm text-slate-600">
            <div>
              <span className="font-semibold text-slate-700">Primary:</span>{' '}
              {lead.assigned_to?.name ?? 'Unassigned'}
            </div>
            <div>
              <span className="font-semibold text-slate-700">Secondary:</span>{' '}
              {lead.assigned_to_secondary?.name ?? 'Unassigned'}
            </div>
          </div>
        </div>
      </SurfaceCard>

      <RelatedAccountsPanel merchantId={merchant.id} />

      <LeadAccountWorkspace leadId={lead.id} showHeader={false} showSharedSections={false} />

      <AppointmentsPanel anchor={{ kind: 'lead', id: lead.id }} />
      <AccountActivityPanel anchor={{ kind: 'lead', id: lead.id }} />
      <AppointmentsPanel anchor={{ kind: 'merchant', id: merchant.id }} />
      <AccountActivityPanel anchor={{ kind: 'merchant', id: merchant.id }} />

      <DocumentCenter
        context={sharedAnchor.kind === 'lead'
          ? { kind: 'lead', lead }
          : { kind: 'merchant', merchant: merchant as Merchant }}
      />
      <PendingDocumentRequests anchor={sharedAnchor} />
      <NotesPanel anchor={notesAnchor} />

      <MerchantAccountWorkspace merchantId={merchant.id} showHeader={false} showSharedSections={false} showRelatedAccounts={false} />
    </div>
  )
}

export default AccountPage
